import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

function postHeight(formId) {
  const height = Math.max(document.documentElement.scrollHeight, 280);
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      { type: 'mailsball-embed-resize', formId, height },
      '*',
    );
  }
}

export default function EmbedForm() {
  const { publicId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const viewTracked = useRef(false);

  const theme = form?.theme || {};
  const primaryColor = theme.primaryColor || '#300259';
  const backgroundColor = theme.backgroundColor || '#ffffff';
  const textColor = theme.textColor || '#2e2e2e';
  const borderRadius = theme.borderRadius || '8px';

  const notifyResize = useCallback(() => {
    if (publicId) postHeight(publicId);
  }, [publicId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/api/public/forms/${encodeURIComponent(publicId)}`);
        if (!cancelled) {
          setForm(res.data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Form not found');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [publicId]);

  useEffect(() => {
    if (!form || viewTracked.current) return;
    viewTracked.current = true;
    api
      .post(`/api/public/forms/${encodeURIComponent(publicId)}/view`, {
        referrer: document.referrer || '',
      })
      .catch(() => {});
  }, [form, publicId]);

  useEffect(() => {
    notifyResize();
    const timer = setTimeout(notifyResize, 100);
    return () => clearTimeout(timer);
  }, [loading, error, success, form, notifyResize]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await api.post(
        `/api/public/forms/${encodeURIComponent(publicId)}/subscribe`,
        {
          email,
          firstName,
          lastName,
          consent,
          referrer: document.referrer || '',
          website: '',
        },
      );

      setSuccess(res.data.message || 'Thank you!');
      notifyResize();

      if (res.data.redirectUrl) {
        setTimeout(() => {
          window.top.location.href = res.data.redirectUrl;
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Subscription failed');
      notifyResize();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
        Loading…
      </div>
    );
  }

  if (!form) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#c62828' }}>
        {error || 'Form not available'}
      </div>
    );
  }

  if (success) {
    return (
      <div
        style={{
          padding: 24,
          background: backgroundColor,
          color: textColor,
          fontFamily: 'Arial, sans-serif',
          borderRadius,
        }}
      >
        <p style={{ margin: 0, textAlign: 'center', lineHeight: 1.5 }}>{success}</p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 24,
        background: backgroundColor,
        color: textColor,
        fontFamily: 'Arial, sans-serif',
        borderRadius,
        boxSizing: 'border-box',
      }}
    >
      <form onSubmit={handleSubmit} noValidate>
        {form.fields?.firstName?.enabled ? (
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
              {form.fields.firstName.label || 'First name'}
              {form.fields.firstName.required ? ' *' : ''}
            </span>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required={form.fields.firstName.required}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius,
                boxSizing: 'border-box',
                fontSize: 15,
              }}
            />
          </label>
        ) : null}

        {form.fields?.lastName?.enabled ? (
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
              {form.fields.lastName.label || 'Last name'}
              {form.fields.lastName.required ? ' *' : ''}
            </span>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required={form.fields.lastName.required}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius,
                boxSizing: 'border-box',
                fontSize: 15,
              }}
            />
          </label>
        ) : null}

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
            {form.fields?.email?.label || 'Email'} *
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ccc',
              borderRadius,
              boxSizing: 'border-box',
              fontSize: 15,
            }}
          />
        </label>

        {form.requireConsent ? (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 16,
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
              style={{ marginTop: 3 }}
            />
            <span>
              {form.consentText}
              {form.privacyPolicyUrl ? (
                <>
                  {' '}
                  <a
                    href={form.privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: primaryColor }}
                  >
                    Privacy policy
                  </a>
                </>
              ) : null}
            </span>
          </label>
        ) : null}

        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            width: 1,
            height: 1,
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </div>

        {error ? (
          <p style={{ color: '#c62828', fontSize: 14, margin: '0 0 12px' }}>{error}</p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius,
            fontSize: 16,
            fontWeight: 600,
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? 'Submitting…' : theme.buttonText || 'Subscribe'}
        </button>
      </form>
    </div>
  );
}
